import React, { useEffect, useRef } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useSpring,
  useReducedMotion,
} from "framer-motion";
import { ArrowRight, Play } from "lucide-react";

/**
 * Cinematic, editorial Hero.
 *  - Full-bleed looping video (DMX console render) as the background.
 *  - Heavy dark gradient + vignette for legibility.
 *  - Headline / eyebrow / CTAs sit on the lower-left like an awards site.
 *  - Subtle parallax + zoom on the video as the user scrolls.
 */
export const Hero = ({ onScrollProgress }) => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const videoRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const smooth = useSpring(scrollYProgress, {
    stiffness: 80,
    damping: 22,
    mass: 0.5,
  });
  useEffect(
    () => smooth.on("change", (v) => onScrollProgress?.(v)),
    [smooth, onScrollProgress]
  );

  // Subtle parallax + scale on the video as we scroll
  const videoY = useTransform(smooth, [0, 1], ["0%", "12%"]);
  const videoScale = useTransform(smooth, [0, 1], [1.04, 1.12]);
  const overlayOpacity = useTransform(smooth, [0, 0.6], [0.55, 0.95]);

  // Make sure the video plays even when autoPlay is blocked + slow it down for cinematic feel
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.playbackRate = 0.65;
    const tryPlay = () => {
      v.playbackRate = 0.65;
      v.play().catch(() => {});
    };
    tryPlay();
    v.addEventListener("loadedmetadata", () => {
      v.playbackRate = 0.65;
    });
    document.addEventListener("visibilitychange", tryPlay);
    return () => document.removeEventListener("visibilitychange", tryPlay);
  }, []);

  const headlineWords = ["Wir", "inszenieren", "unvergessliche", "Momente."];

  return (
    <section
      ref={ref}
      id="hero"
      className="relative w-full overflow-hidden"
      style={{ minHeight: "100vh" }}
      data-testid="hero-section"
    >
      {/* ── Background video ─────────────────────────────────────── */}
      <motion.div
        className="absolute inset-0 z-0"
        style={{ y: videoY, scale: videoScale }}
        aria-hidden
      >
        <video
          ref={videoRef}
          className="w-full h-full object-cover"
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          poster="/_frame.jpg"
          data-testid="hero-video"
        >
          <source src="/pult-loop.webm" type="video/webm" />
          <source src="/pult-loop.mp4" type="video/mp4" />
        </video>
      </motion.div>

      {/* ── Cinematic dark overlays ──────────────────────────────── */}
      {/* Base darken */}
      <div
        className="absolute inset-0 z-[1] pointer-events-none"
        style={{ background: "rgba(6,6,8,0.45)" }}
        aria-hidden
      />
      {/* Vertical fade — keeps top dim, bottom heavy & blends into next section */}
      <div
        className="absolute inset-0 z-[2] pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(6,6,8,0.65) 0%, rgba(6,6,8,0.15) 30%, rgba(6,6,8,0.55) 65%, rgba(6,6,8,1) 100%)",
        }}
        aria-hidden
      />
      {/* Extra bottom blend — guarantees a smooth handover into Services */}
      <div
        className="absolute left-0 right-0 z-[3] pointer-events-none"
        style={{
          bottom: 0,
          height: "26vh",
          background:
            "linear-gradient(180deg, rgba(6,6,8,0) 0%, rgba(6,6,8,0.7) 55%, #06060a 100%)",
        }}
        aria-hidden
      />
      {/* Left editorial fade — anchors the text */}
      <div
        className="absolute inset-0 z-[2] pointer-events-none hidden md:block"
        style={{
          background:
            "linear-gradient(90deg, rgba(6,6,8,0.85) 0%, rgba(6,6,8,0.45) 35%, transparent 65%)",
        }}
        aria-hidden
      />
      {/* Vignette */}
      <div
        className="absolute inset-0 z-[2] pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.55) 100%)",
        }}
        aria-hidden
      />
      {/* Amber glow — bottom edge */}
      <div
        className="absolute z-[2] pointer-events-none"
        style={{
          left: "-10%",
          right: "-10%",
          bottom: "-15%",
          height: "45%",
          background:
            "radial-gradient(ellipse at center, rgba(244,152,0,0.20), transparent 65%)",
          filter: "blur(40px)",
          mixBlendMode: "screen",
        }}
        aria-hidden
      />
      {/* Scroll-driven extra darkening */}
      <motion.div
        className="absolute inset-0 z-[3] pointer-events-none"
        style={{ background: "#06060a", opacity: overlayOpacity }}
        aria-hidden
      />

      {/* ── Outline wordmark (huge, behind text) ────────────────── */}
      <motion.div
        className="absolute left-0 right-0 flex justify-center pointer-events-none select-none z-[4]"
        style={{ bottom: "-4vh" }}
        animate={reduce ? {} : { opacity: [0.05, 0.1, 0.05] }}
        transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
        aria-hidden
      >
        <span
          className="outline-wordmark"
          style={{ fontSize: "clamp(160px, 28vw, 520px)", lineHeight: 0.85 }}
        >
          monoton
        </span>
      </motion.div>

      {/* ── Editorial headline block, lower-left ────────────────── */}
      <div
        className="relative z-10 px-6 sm:px-10 lg:px-14 mt-[18vh] sm:mt-[22vh] pb-[12vh]"
        style={{ maxWidth: "min(900px, 64vw)" }}
      >
        <motion.p
          className="eyebrow mb-6"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          data-testid="hero-eyebrow"
        >
          Light · Sound · Stage
        </motion.p>

        <h1 className="h1-display" data-testid="hero-headline">
          <span className="block">
            {headlineWords.slice(0, 2).map((w, i) => (
              <motion.span
                key={i}
                className="inline-block mr-[0.22em]"
                initial={reduce ? { opacity: 0 } : { opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.8,
                  delay: 0.35 + i * 0.08,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                {w}
              </motion.span>
            ))}
          </span>
          <span className="block">
            {headlineWords.slice(2).map((w, i) => (
              <motion.span
                key={i}
                className="inline-block mr-[0.22em]"
                initial={reduce ? { opacity: 0 } : { opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.8,
                  delay: 0.35 + (i + 2) * 0.08,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                {w}
              </motion.span>
            ))}
          </span>
        </h1>

        <motion.p
          className="mt-7 text-base sm:text-lg body-mute max-w-[480px]"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.95, ease: [0.22, 1, 0.36, 1] }}
        >
          Von der privaten Feier bis zur Großveranstaltung – wir liefern professionelle Licht-, Ton- und Bühnentechnik für Events, die in Erinnerung bleiben.
        </motion.p>

        <motion.div
          className="mt-10 flex flex-wrap items-center gap-3"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
        >
          <button
            onClick={() =>
              document
                .getElementById("contact")
                ?.scrollIntoView({ behavior: "smooth" })
            }
            className="btn-primary"
            data-testid="hero-cta-primary"
          >
            Projekt anfragen <ArrowRight size={16} />
          </button>
          <button
            onClick={() =>
              document
                .getElementById("projects")
                ?.scrollIntoView({ behavior: "smooth" })
            }
            className="btn-ghost"
            data-testid="hero-cta-secondary"
          >
            <Play size={14} /> Showreel ansehen
          </button>
        </motion.div>
      </div>

      {/* ── Bottom-right meta: live indicator ───────────────────── */}
      <div
        className="absolute z-10 flex items-center gap-2"
        style={{ right: "1.5rem", bottom: "1.5rem" }}
        data-testid="live-indicator"
      >
        <span className="live-dot animate-pulse-dot" />
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-mute">
          Ready · CH 1—32
        </span>
      </div>

      {/* ── Bottom-left meta: scroll hint ───────────────────────── */}
      <motion.div
        className="absolute z-10 hidden sm:flex items-center gap-3"
        style={{ left: "1.5rem", bottom: "1.5rem" }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.6 }}
        data-testid="scroll-hint"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-mute">
          Scroll
        </span>
        <motion.span
          className="block w-10 h-px bg-white/40"
          animate={reduce ? {} : { scaleX: [0.4, 1, 0.4], originX: 0 }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.div>
    </section>
  );
};

export default Hero;
